/**
 * Shared engine-level input state (docs/editor-consolidation/
 * hollowscript-capabilities-v1.md section 3.5): a single, engine-owned
 * source of truth for "is this physical input currently active", combining
 * keyboard, gamepad, and touch. Before this module, three runtime files each
 * tracked their own keyboard state independently and disagreed with each
 * other about almost everything except "keys are strings":
 * `FirstPersonController.tsx`'s own `keys` ref plus its own window
 * listeners, `Platformer2DScene.tsx`'s private `useInput()` (a second,
 * differently-shaped keyboard Set, plus its own gamepad polling), and
 * `TouchControls.tsx` (touch bridged in as synthesized keyboard events, so
 * it never needed a listener of its own at all - see `dispatchVirtualKey`
 * below for why that mechanism is kept, not replaced).
 *
 * Deliberately a plain module, not a Zustand store like every other piece of
 * engine runtime state (`engineRuntimeStore.ts`, `liveSceneStore.ts`):
 * nothing here needs a React re-render on change - every consumer polls it
 * once per `useFrame` tick, exactly like the private refs it replaces did.
 *
 * Scope: this module tracks RAW physical/virtual input only - nothing about
 * which game mode, editor state, or pause state currently means "ignore it".
 * That gating is domain-specific and stays with each consumer (see
 * `FirstPersonController.tsx`'s `inputSuppressed` local, and
 * `Platformer2DScene.tsx`'s `isTypingIntoFormField()` check). A HollowScript
 * `uses input` script reading this state (via `scripting/capabilities.ts`'s
 * `inputCapability`) sees real physical/virtual input regardless of which
 * controller currently chooses to act on it.
 */

// ── Keyboard ────────────────────────────────────────────────────────────────

const keysDown = new Set<string>();
const keysJustPressed = new Set<string>();
let keyboardListenerRefCount = 0;

function handleWindowKeyDown(event: KeyboardEvent): void {
  if (!keysDown.has(event.code)) keysJustPressed.add(event.code);
  keysDown.add(event.code);
}

function handleWindowKeyUp(event: KeyboardEvent): void {
  keysDown.delete(event.code);
}

/**
 * Starts (or joins) the shared keyboard listener. Call once per mounted
 * consumer in a `useEffect` on mount, paired with `releaseKeyboardInput()`
 * on unmount. Reference-counted so e.g. `FirstPersonController` and
 * `TouchControlsOverlay` mounted at the same time share one real `window`
 * listener instead of each attaching their own.
 */
export function acquireKeyboardInput(): void {
  keyboardListenerRefCount += 1;
  if (keyboardListenerRefCount > 1) return;
  if (typeof window === 'undefined') return;
  window.addEventListener('keydown', handleWindowKeyDown);
  window.addEventListener('keyup', handleWindowKeyUp);
}

/**
 * Pairs with `acquireKeyboardInput()`. Once the last consumer releases, the
 * real listener is torn down and state is cleared, so a later fresh mount
 * starts clean - matching the old per-component ref's implicit reset on
 * remount (a brand new `useRef({...})`), now that the state itself is a
 * shared singleton rather than something each component instance owned.
 */
export function releaseKeyboardInput(): void {
  keyboardListenerRefCount = Math.max(0, keyboardListenerRefCount - 1);
  if (keyboardListenerRefCount > 0) return;
  if (typeof window !== 'undefined') {
    window.removeEventListener('keydown', handleWindowKeyDown);
    window.removeEventListener('keyup', handleWindowKeyUp);
  }
  keysDown.clear();
  keysJustPressed.clear();
}

/** True while `code` (a `KeyboardEvent.code`, e.g. `"KeyW"`) is currently
 * held - physically, or virtually via `dispatchVirtualKey` (touch). */
export function isKeyDown(code: string): boolean {
  return keysDown.has(code);
}

/** True if `code` went from up to down since the last `clearJustPressedKeys()`
 * call. Non-consuming ("peek") by design - see that function's own comment
 * for why. */
export function isKeyJustPressed(code: string): boolean {
  return keysJustPressed.has(code);
}

/**
 * Clears every pending just-pressed edge. Call once per rendered frame,
 * after checking every code of interest that frame - mirrors
 * `Platformer2DScene`'s pre-consolidation `justPressed` Set exactly (each
 * code checked with `.has()`, the whole Set `.clear()`ed once at the end of
 * the frame). A per-code "consume on read" API was deliberately rejected in
 * favour of this: that consumer's own checks OR several codes together
 * (`jp.has('Space') || jp.has('KeyW') || jp.has('ArrowUp') || ...`), and
 * `||` short-circuits - consuming only the first true code's edge would
 * leave any other genuinely-pressed code's edge dangling into the next
 * frame, wrongly firing there too.
 */
export function clearJustPressedKeys(): void {
  keysJustPressed.clear();
}

/**
 * True while the currently focused element is a text-entry field (`<input>`
 * or `<textarea>`) - the same guard `Platformer2DScene`'s pre-consolidation
 * `useInput()` applied at its keydown listener (skipping capture entirely
 * while typing, so movement/ability keys never fired while e.g. a dev-tool
 * text field had focus). Applied here at the read site instead, now that the
 * shared listener captures unconditionally for every consumer:
 * `FirstPersonController` never had this guard and does not gain it here -
 * only `Platformer2DScene` checks it, preserving each consumer's own prior
 * behaviour exactly rather than imposing one opinion on both.
 */
export function isTypingIntoFormField(): boolean {
  if (typeof document === 'undefined') return false;
  const active = document.activeElement;
  if (!active) return false;
  return active.tagName === 'INPUT' || active.tagName === 'TEXTAREA';
}

/**
 * Forces every currently-held key (physical or virtual) up at once, and
 * drops any pending just-pressed edges too. For a consumer's own "the window
 * just lost focus, stop trusting held keys" handling
 * (`FirstPersonController`'s `blur` listener) - a real `keyup` for a
 * physically-held key is not guaranteed to arrive after e.g. an alt-tab on
 * every platform.
 */
export function clearAllKeysDown(): void {
  keysDown.clear();
  keysJustPressed.clear();
}

/**
 * Synthesizes a real `KeyboardEvent` on `window` for `code` - the mechanism
 * `TouchControls.tsx` uses to drive movement/action keys from touch. A real
 * DOM event, not a private write into this module's own `keysDown`/
 * `keysJustPressed` state, deliberately: it has to reach every keyboard
 * listener in the engine, including ones that have not (yet, or ever)
 * migrated onto this module - `Tilemap2DScene.tsx`'s own private keydown
 * listener, for one, is untouched by this consolidation and still depends
 * on exactly this mechanism to receive touch input. One dispatch reaches
 * every keyboard-driven controller, migrated or not; a private state write
 * would only reach this module's own listener.
 */
export function dispatchVirtualKey(type: 'keydown' | 'keyup', code: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new KeyboardEvent(type, { code, bubbles: true }));
}

/**
 * Test-only: fully resets keyboard state and the listener ref count between
 * test cases, mirroring `resetLiveSceneStore`'s role for that store. Not for
 * production use - production consumers only ever add/remove their own
 * interest via `acquireKeyboardInput`/`releaseKeyboardInput`.
 */
export function resetInputStateForTests(): void {
  if (typeof window !== 'undefined' && keyboardListenerRefCount > 0) {
    window.removeEventListener('keydown', handleWindowKeyDown);
    window.removeEventListener('keyup', handleWindowKeyUp);
  }
  keyboardListenerRefCount = 0;
  keysDown.clear();
  keysJustPressed.clear();
}

// ── Gamepad ─────────────────────────────────────────────────────────────────

function gamepadApiAvailable(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.getGamepads === 'function';
}

/** Every currently connected gamepad. `[]` in any environment without the
 * Gamepad API (SSR, most test environments) rather than throwing. */
export function getConnectedGamepads(): Gamepad[] {
  if (!gamepadApiAvailable()) return [];
  const pads: Gamepad[] = [];
  for (const pad of navigator.getGamepads()) {
    if (pad && pad.connected) pads.push(pad);
  }
  return pads;
}

/** The first connected gamepad - matches the "player 1" convention
 * `Platformer2DScene.tsx`'s own polling already used
 * (`gamepads[0] || gamepads[1] || gamepads[2] || gamepads[3]`), or `null` if
 * none is connected. */
export function getPrimaryGamepad(): Gamepad | null {
  if (!gamepadApiAvailable()) return null;
  const pads = navigator.getGamepads();
  return pads[0] || pads[1] || pads[2] || pads[3] || null;
}
